'use client'

import { useState, useRef, useEffect } from 'react'
import { ImageCropperOverlay } from '@/components/ImageCropperOverlay'
import { ImageLightbox } from '@/components/ImageLightbox'
import { validateImageFile } from '@/utils/fileValidation'
import { toast } from 'sonner'
import Image from 'next/image'
import { HugeiconsIcon } from '@hugeicons/react'
import { Camera01Icon, Delete01Icon as Delete01IconSolid, PencilEdit01Icon } from '@hugeicons-pro/core-solid-rounded'
import { Button } from './ui/button'
import { Label } from './ui/label'

interface AvatarUploadTriggerProps {
  // Avatar state - can be File, base64 string, or URL string
  avatarFile?: File | null
  avatarBase64?: string | null
  avatarUrl?: string | null
  
  // Component configuration
  inputId: string
  label?: string
  disabled?: boolean
  submitting?: boolean
  
  // Callbacks
  onFileSelect?: (file: File) => void
  onCropComplete: (croppedFile: File) => void
  onClear: () => void
  
  // Optional styling
  className?: string
}

export function AvatarUploadTrigger({
  avatarFile,
  avatarBase64,
  avatarUrl,
  inputId,
  label,
  disabled = false,
  submitting = false,
  onFileSelect,
  onCropComplete,
  onClear,
  className = ''
}: AvatarUploadTriggerProps) {
  const [showAvatarCropper, setShowAvatarCropper] = useState(false)
  const [showLightbox, setShowLightbox] = useState(false)
  const [originalAvatarImageUrl, setOriginalAvatarImageUrl] = useState<string | null>(null)
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Create and cleanup object URL for file preview
  useEffect(() => {
    if (avatarFile) {
      const url = URL.createObjectURL(avatarFile)
      setFilePreviewUrl(url)
      return () => {
        URL.revokeObjectURL(url)
      }
    } else {
      setFilePreviewUrl(null)
    }
  }, [avatarFile])

  // Determine the preview source
  const getPreviewSource = (): string | null => {
    if (filePreviewUrl) {
      return filePreviewUrl
    }
    if (avatarBase64) {
      return avatarBase64
    }
    if (avatarUrl) {
      return avatarUrl
    }
    return null
  }

  const previewSource = getPreviewSource()
  const hasAvatar = !!previewSource

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate the file
    const validation = validateImageFile(file)
    if (!validation.isValid) {
      toast.error(validation.error || 'Invalid file')
      e.target.value = ''
      return
    }

    // Call onFileSelect callback if provided
    onFileSelect?.(file)

    // Create URL for the cropper
    const url = URL.createObjectURL(file)
    setOriginalAvatarImageUrl(url)
    setShowAvatarCropper(true)
  }

  const handleAvatarCropComplete = async (croppedBlob: Blob) => {
    setShowAvatarCropper(false)
    
    // Convert blob to file
    const file = new File([croppedBlob], 'avatar.jpg', { type: 'image/jpeg' })
    
    // Call the crop complete callback
    onCropComplete(file)
    
    // Clean up the original image URL
    if (originalAvatarImageUrl) {
      URL.revokeObjectURL(originalAvatarImageUrl)
      setOriginalAvatarImageUrl(null)
    }
  }

  const handleAvatarCropCancel = () => {
    setShowAvatarCropper(false)
    if (originalAvatarImageUrl) {
      URL.revokeObjectURL(originalAvatarImageUrl)
      setOriginalAvatarImageUrl(null)
    }
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClick = () => {
    if (disabled || submitting) return
    fileInputRef.current?.click()
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    // Clear file preview URL
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl)
      setFilePreviewUrl(null)
    }
    onClear()
  }

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasAvatar && previewSource) {
      setShowLightbox(true)
    }
  }

  const isDisabled = disabled || submitting

  return (
    <>
      <div className={`w-full space-y-1 ${className}`}>
        {label && (
          <Label variant="atrium" htmlFor={inputId} className="mb-1.5">{label}</Label>
        )}
        <div className="flex items-center">
          <div 
              onClick={!isDisabled ? handleAvatarClick : undefined}
              className={`
                flex justify-center items-center w-12 h-12 bg-atrium-black shadow-sm rounded-full shrink-0 relative overflow-hidden
                ${hasAvatar ? 'cursor-pointer' : ''},
                ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}
              `}
            >
              {hasAvatar ? (
                <Image
                  src={previewSource}
                  alt="Avatar preview"
                  fill
                  className="object-cover"
                />
              ) : (
                <HugeiconsIcon icon={Camera01Icon} size={16} className="text-white" />
              )}
            </div>

            <Button 
              type="button"
              onClick={handleClick}
              disabled={isDisabled}
              variant="outline"
              className="ml-2 hover:bg-atrium-content hover:text-accent-600 text-atrium-black transition-colors duration-150 disabled:pointer-events-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-atrium-black"
            >
              {hasAvatar && (
                <HugeiconsIcon icon={PencilEdit01Icon} size={16} />
              )}
              <span className="text-sm font-medium">
                {hasAvatar ? 'Edit avatar' : 'Choose avatar'}
              </span>
            </Button>

          {hasAvatar && (
            <Button
              type="button"
              variant="outline"
              onClick={handleClear}
              disabled={isDisabled}
              className="text-atrium-black/60 hover:text-red-500 hover:bg-red-50 ml-1 hover:bg-atrium-content transition-colors duration-150 disabled:pointer-events-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-atrium-black/60"
            >
              <HugeiconsIcon icon={Delete01IconSolid} size={16} />
            </Button>
          )}
          <input
            ref={fileInputRef}
            id={inputId}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
            onChange={handleFileChange}
            className="hidden"
            disabled={isDisabled}
          />
        </div>
      </div>

      {/* Avatar Cropper Overlay */}
      <ImageCropperOverlay
        imageUrl={originalAvatarImageUrl || ''}
        onCropComplete={handleAvatarCropComplete}
        onCancel={handleAvatarCropCancel}
        isOpen={showAvatarCropper}
        aspectRatio={1}
      />

      {/* Image Lightbox */}
      {showLightbox && previewSource && (
        <ImageLightbox
          imageUrl={previewSource}
          onClose={() => setShowLightbox(false)}
        />
      )}
    </>
  )
}
